{
  "discounts_upsell": {
    "title": "Manage Discounts",
    "subtitle": "Discounts let you set up promotions for your B2B customers (independent of your B2C customers) that work alongside any price list rules you have set up.",
    "learn_button": "Learn more",
    "intro": "Here are just some of the things you can do with the SparkLayer Discount Engine:",
    "features": {
      "discount_types": {
        "title": "Automatic or coupon-based discounts",
        "description": "Set discounts to apply automatically when conditions are met, or let customers enter coupon codes at checkout."
      },
      "targeted_discounts": {
        "title": "Target by customer group, order value, or products",
        "description": "Apply discounts to specific B2B customer groups, set minimum order thresholds, or target specific SKUs and product tags."
      },
      "reward_discounts": {
        "title": "Free products, shipping rewards, and more",
        "description": "Go beyond percentage-off - add free products to orders, offer free shipping over a threshold, or set fixed-price shipping for your wholesale buyers."
      }
    }
  },
  "list_table": {
    "caption": "Discounts",
    "views": {
      "all": "All",
      "active": "Active",
      "inactive": "Inactive"
    },
    "tooltips": {
      "coupon-required": "This discount requires the customer to enter a coupon code:",
      "group-required": "This discount requires the customer to belong to a specific customer group:",
      "scheduling-required": "This discount will be active {range, select, both {from <strong>{start}</strong> until <strong>{end}</strong>} start {from <strong>{start}</strong>} other {until <strong>{end}</strong>}}.",
      "spend-required": "This discount will be applied only if the cart sub-total is {range, select, both {at least <strong>{min}</strong> and lower than <strong>{max}</strong>} min {at least <strong>{min}</strong>} other {lower than <strong>{max}</strong>}}.",
      "num-items-required": "This discount will be applied only if the cart items quantity is {range, select, both {at least <strong>{min}</strong> and lower than <strong>{max}</strong>} min {at least <strong>{min}</strong>} other {lower than <strong>{max}</strong>}}.",
      "max-uses-required": "Customers can use this discount a maximum of <strong>{count, plural, =1 {# time} other {# times}}</strong>.",
      "excluded-customers-required": "Some customers are excluded from this discount.",
      "products-required": "This discount requires specific products to be in the cart.",
      "simultaneity-required": "The following discounts may also be used when this discount is applied:",
      "simultaneity-required-all": "All discounts can be used with this discount.",
      "currency-required": "This discount is only valid for orders in the <strong>{currency}</strong> currency."
    },
    "headings": {
      "internal_name": "internal name",
      "type": "type",
      "requirements": "requirements",
      "status": "status"
    },
    "search": "Search discounts",
    "filters": {
      "type": "Type",
      "types": {
        "order_level_get_amount_off": "Give an amount off an order",
        "order_level_get_free_product": "Give a free product",
        "product_level_get_amount_off": "Give a percentage off products",
        "order_level_get_shipping_reward": "Give a shipping reward",
        "advanced_order_level_get_amount_off": "Give an amount off an order with advanced requirements",
        "order_level_requirement_amount_off": "Requirements",
        "advanced_discount": "Advanced requirements and rewards",
        "advanced_free_products": "Advanced free products"
      }
    },
    "sort": {
      "default": "Priority",
      "name": "Name"
    }
  },
  "discounts_delete_modal": {
    "title": "Delete discount",
    "description": "Are you sure you want to delete the \"{name}\" discount?",
    "status": {
      "errors": {
        "delete_failed": "Failed to delete discount."
      },
      "success": {
        "delete_succeeded": "Discount \"{name}\" successfully deleted."
      }
    }
  },
  "builder": {
    "summary": {
      "title": "Summary",
      "tooltip": "Set up the basic information for your discount.",
      "enable": "Enable this discount",
      "enable_description": "Please note that a discount is only active when enabled and the date falls within the scheduled period.",
      "internal_name": "Discount Internal Name",
      "internal_name_placeholder": "e.g. 20% off orders for new B2B customers",
      "internal_name_description": "This is only visible to you.",
      "slug": "Discount Handle (or ID)",
      "slug_placeholder": "e.g. 20-0ff-orders",
      "slug_description": "This is used to identify the discount within your store when an order is placed.",
      "public_name": "Discount Public Name",
      "public_name_placeholder": "e.g. 20% off your first B2B order",
      "public_name_description": "This is shown to your customers when a discount is applied to their order.",
      "language_string": "You can show a description to your customers by adding a language string of discount.{slug}.description to your website.",
      "errors": {
        "slug_invalid_chars": "Discount handle may only contain letters, numbers, dashes and underscores."
      }
    },
    "type": {
      "title": "Type",
      "tooltip": "Choose how you want your discount to work.",
      "upgrade_required": "Upgrade to {plan}",
      "internal_shipping_only": "Only available if you're using SparkLayer shipping",
      "confirm_title": "Change discount type?",
      "confirm_message": "Changing the type clears the reward and requirements already configured for this discount. Any rewards, rules or requirements set up for this discount will be removed.",
      "confirm_confirm": "Change type"
    },
    "rewards": {
      "title": "Reward",
      "tooltip": "Choose the reward you would like to apply to the customer's order.",
      "select_type_first": "Please select a discount type first.",
      "percentage": "Percentage discount (e.g. 10% off)",
      "fixed_amount": "Fixed amount discount (e.g. {symbol}100 off)",
      "percentage_off": "Percentage off",
      "fixed_off_subtotal": "Fixed amount off order sub-total",
      "net_helper": "This will be applied against the sub-total of the order excluding tax (\"net\").",
      "product_sku": "Product SKU",
      "product_sku_placeholder": "Please enter a product SKU",
      "quantity": "Quantity",
      "free_product_helper": "Please specify the SKU of the product that should be added to the order at no additional cost to the customer.",
      "max_skus": "You can only have a maximum of 25 product SKUs",
      "product_sku_helper": "Please specify the SKU of the product to be discounted.",
      "add": "Add",
      "sku": "SKU",
      "product_rewards": "Product rewards",
      "shipping_method": "Shipping Method",
      "shipping_method_label": "Shipping method",
      "no_shipping_methods": "No available shipping methods",
      "shipping_discount_type": "Shipping Discount Type",
      "fixed_charge": "Set the charge to a fixed amount (e.g. {symbol}15.99)",
      "percentage_off_shipping": "Percentage amount off shipping charge",
      "fixed_off_shipping": "Fixed amount off shipping charge",
      "set_fixed_charge": "Set the shipping charge to a fixed amount",
      "shipping_net_helper": "This will be applied against the shipping amount excluding tax (\"net\").",
      "errors": {
        "percentage_range": "Percentage amount must be greater than 0 and no more than 100.",
        "amount_positive": "Amount must be greater than 0.",
        "valid_reward_amount": "Please enter a valid reward amount",
        "valid_condition_value": "Please enter a valid condition value",
        "valid_quantity": "Please enter a valid quantity",
        "specify_reward": "Please specify the discount reward.",
        "no_shipping_methods": "Please ensure you have a shipping method available and it is selected in the discount reward."
      },
      "requirement_helper": "This reward will be applied when the requirement above is met."
    },
    "requirements": {
      "title": "Requirements",
      "tooltip": "Set additional configurations on how you want your discount to work.",
      "upgrade_note": "Please note, this feature is only available on our Growth, Pro and Enterprise plans.",
      "available_on_growth": "Available on Growth, Pro and Enterprise",
      "errors": {
        "confirm_coupon": "Please click 'Add coupon' to confirm your coupon code."
      },
      "coupons": {
        "title": "Coupon codes",
        "description": "Require a coupon code to use this discount",
        "tooltip": "If a coupon is specified, a coupon entry field will show during the checkout.",
        "add_label": "Add a coupon code",
        "placeholder": "e.g. MYDISCOUNT",
        "add": "Add coupon",
        "helper": "If a coupon is specified, a coupon entry field will show during the checkout."
      },
      "coupon_invalid": "Coupon codes can only contain letters, numbers, underscores, and hyphens. No spaces or other special characters are allowed.",
      "coupon_duplicate": "This coupon code has already been added.",
      "groups": {
        "title": "Customer groups",
        "description": "Limit discount to specific customer groups",
        "tooltip": "This discount will be applied only for the customer groups selected. If you would like to apply it to all customers, please turn off this requirement.",
        "label": "Customer groups",
        "helper": "This discount will be applied only for the customer groups selected above. If you would like to apply it to all customers, you can turn off this requirement."
      },
      "scheduling": {
        "title": "Scheduling",
        "description": "Limit the discount to a specific date range",
        "tooltip": "This discount will use the date and time in your current timezone of {timeZone}.",
        "start_label": "Start date of discount",
        "end_label": "Expiry date of discount",
        "timezone": "This discount will use the date and time in your current timezone of {timeZone}."
      },
      "spend": {
        "title": "Order sub-total limits",
        "description": "Require a minimum or maximum order sub-total",
        "tooltip": "The discount will apply if the customer's order sub-total is between these amounts (\"net\").",
        "min": "Min. order sub-total",
        "max": "Max. order sub-total",
        "helper": "The discount will apply if the customer's order sub-total is between these amounts (\"net\"). For example, if the maximum order sub-total is 100, the discount will apply to orders up to and including 99.99 (but not 100)."
      },
      "items": {
        "title": "Order item limits",
        "description": "Require a minimum or maximum number of items",
        "tooltip": "The discount will apply if the customer's order matches these limits.",
        "how_counted": "How order quantities should be determined",
        "total_quantity": "Quantity across all line items",
        "total_lines": "Number of line items",
        "min": "Require at least",
        "max": "Up to",
        "number_placeholder": "Enter number of items",
        "helper": "Choose to count either the combined quantity of all items or the number of line items. The discount will apply if the quantity is between these amounts."
      },
      "usage": {
        "title": "Usage limits",
        "description": "Limit number of uses per customer",
        "tooltip": "The discount won't be applied if the customer has reached the usage limit.",
        "max_uses": "Maximum uses per customer",
        "number_placeholder": "Enter number of uses",
        "helper": "The discount will apply if the customer has used it less than the number specified above."
      },
      "exclude": {
        "title": "Exclude customers",
        "description": "Exclude specific customers from this discount",
        "tooltip": "Prevent specific customers from using this discount.",
        "label": "Excluded customers",
        "placeholder": "Enter email addresses or accounting IDs, separated by commas",
        "helper": "The discount will not apply to customers in this list."
      },
      "compatible": {
        "title": "Compatible discounts",
        "description": "Allow this discount to be used in conjunction with others. By default, customers cannot apply more than one discount to their order.",
        "tooltip": "Select all the discounts that can be used alongside this discount within the same order.",
        "label": "Compatible discounts",
        "placeholder": "Select compatible discounts",
        "helper": "Select all the discounts that can be used alongside this discount within the same order. If none are selected all discounts may be used."
      },
      "currency": {
        "title": "Currency",
        "tooltip": "The customer must be placing an order in the selected currency.",
        "label": "Currency",
        "description": "The customer must be placing an order in the selected currency."
      }
    },
    "rules": {
      "title": "Rules",
      "tooltip": "Discount rules let you restrict when a discount is applied by requiring specific products to be in the customer's order.",
      "intro": "You can restrict when a discount is applied by requiring the customer to meet specific rules, e.g. which products they must have.",
      "new": "New",
      "max_rules": "You can only have a maximum of 10 rules",
      "min_quantity": "You must specify a quantity of at least 1",
      "min_exceeds_max": "Min spend cannot be greater than Max spend",
      "max_skus": "You can only have a maximum of 10 SKUs",
      "product_rule": "Product Rule {ruleNumber}",
      "skus_required": "Any of these SKUs must be in the customer's order:",
      "sku_placeholder": "Please enter a product SKU",
      "any_item": "Any item",
      "required_quantity": "Required quantity across all SKUs",
      "staff_only_title": "SKU Quantity Discount Rules (staff only)",
      "min_spend": "Min Spend",
      "max_spend": "Max Spend",
      "spend_between": "SKU quantity rules between: {min} – {max}",
      "required_quantity_value": "Required quantity across all SKUs: {quantity}",
      "helper": "This rule will apply providing the customer's order contains any of the SKUs specified above and meets the quantity requirement.",
      "save": "Save",
      "errors": {
        "specify_requirement": "Please specify at least one requirement."
      }
    },
    "errors": {
      "open_edits": "Please save or cancel any open requirements or rewards before saving the discount."
    },
    "advanced_rules": {
      "title": "Advanced Requirements",
      "title_free_products": "Advanced Free Product Requirements",
      "tooltip": "Add advanced filters that must be matched before this discount is applied.",
      "no_requirements": "No requirements defined, please add a new requirement.",
      "add": "Add",
      "staff_only_title": "SKUs sub-total rules (staff only)",
      "min_spend": "Min. sub-total",
      "max_spend": "Max. sub-total",
      "spend_between": "SKU sub-total rules between: {min} – {max}",
      "edit": "Edit",
      "multiple_filters": "Multiple Filters",
      "all_match": "All of the filters must match",
      "minimum_match": "A minimum number of the filters must match",
      "filters_to_match": "Number of filters to match"
    },
    "advanced_rewards": {
      "title": {
        "shipping": "Shipping Reward",
        "subtotal": "Sub-total Reward",
        "cart_lines": "Cart Lines Reward",
        "requirements": "Requirement Based Reward",
        "reward": "Reward"
      },
      "tooltip": "Configure one or more rewards that will be applied when all requirements are met.",
      "no_rewards": "No rewards defined, please add a new reward.",
      "add": {
        "subtotal": "Sub-total",
        "shipping": "Shipping",
        "requirements": "Requirements",
        "cart_lines": "Cart Lines"
      },
      "apply_all": "Apply all rewards",
      "apply_lowest": "Apply the {count} lowest-value {count, plural, =1 {reward} other {rewards}}",
      "apply_highest": "Apply the {count} highest-value {count, plural, =1 {reward} other {rewards}}",
      "max_rewards": "Number of lowest/highest value {count, plural, =1 {reward} other {rewards}}",
      "max_rewards_description": "Sets the maximum of rewards that can be applied via this discount.",
      "apply_to": "Apply this reward to",
      "apply_cheapest": "Apply the least generous matching reward",
      "summary": {
        "subtotal": "This reward applies {appliesText} off the sub-total",
        "shipping_off": "For \"{method}\", this reward applies {appliesText} off the shipping charge",
        "shipping_fixed": "For \"{method}\", this reward sets the shipping charge to {appliesText}",
        "requirements": "This reward applies {appliesText} off the products matching the requirements",
        "cart_lines": "This reward applies {appliesText} off cart lines where {conditionObject} {verb} \"{itemId}\"",
        "reward": "Reward",
        "contains": "contains",
        "does_not_contain": "does not contain",
        "is": "is",
        "is_not": "is not"
      },
      "card_title": "Advanced Rewards"
    },
    "filters": {
      "select_field": "Select Field",
      "select_type": "Select Type",
      "namespace": "Namespace",
      "key": "Key",
      "select_operator": "Select Operator",
      "value": "Value",
      "operators": {
        "equals": "Equals",
        "notEquals": "Doesn't Equal",
        "contains": "Contains",
        "notContains": "Doesn't Contain"
      },
      "metadata": {
        "string": "String",
        "array_of_string": "Array of Strings"
      },
      "minimum_quantity": "Minimum of {count} {count, plural, =1 {item} other {items}} of the cart lines quantity must match:",
      "add_condition": "Add Condition",
      "quantity": "Quantity",
      "restriction": {
        "exclude_sku": "Exclude SKU",
        "skus": "SKUs",
        "containing": "containing",
        "exclude_skus": "Exclude SKUs",
        "exclude": "Exclude"
      }
    },
    "free_products": {
      "apply_label": "Apply the free product rewards:",
      "once": "Once only",
      "recursive": "Recursively - every time the conditions are met",
      "limited": "Limited - every time the conditions are met up to a maximum number of applications",
      "max_applications": "Max number of applications of the free product rewards",
      "sku_helper": "Please specify the SKU of the product to be rewarded for free.",
      "lines": "Free product rewards",
      "card_title": "Advanced Free Product Rewards",
      "card_tooltip": "Choose the reward you would like to apply to the customer's order.",
      "pro_upgrade_note": "Please note, this feature is only available on our Pro and Enterprise plans.",
      "available_on_pro": "Available on Pro and Enterprise"
    }
  },
  "discount_priority_modal": {
    "title": "Manage discount priorities",
    "description": "If you have multiple discounts set up, you can set the priority in which they get applied to a customer's order. You can also manage which discounts are compatible by editing a discount and going to \"Compatible discounts\".",
    "status": {
      "success": {
        "update_succeeded": "Discount priorities successfully updated."
      },
      "errors": {
        "update_failed": "Failed to update discount priorities."
      }
    }
  }
}
